<template>
  <section
    class="public-table media-buying-ad"
    :class="{
      wavebg: loading
    }"
  >
    <TableCard
      ref="datatable"
      v-model:sort="sorting"
      v-model:page="page"
      v-model:page-size="pageSize"
      :loading="loading"
      :columns="columns"
      sort-by-name="prop"
      sort-dir-name="order"
      :rows="rows"
      :count="$store.state.promocode.totalRows"
      :formaters="formaters"
      :actions="actions"
      :header="$t('mediaBuying.campaigns.promo_codes')"
      is-static
      dont-fill-empty
      custom-context
      :disable-actions="isArchived"
      :has-pagination="true"
      placement-tooltip="bottom"
      i18n-path="mediaBuying.promocodes"
      @fetch-data="fetchDataTable"
    >
      <template v-slot:header>
        <div
          :class="[
            'ui-d-flex',
            'ui-desktop-no-wrap',
            'ui-mobile-wrap',
            'ui-mobile-12',
          ]"
        >
          <TableCardSearchHeader
            v-model="search"
            class="ui-g-md"
            @search="handleSearch"
          />
        </div>
      </template>
      <template v-slot:actionsPrepend>
        <div class="ui-g-md ui-mobile-12">
          <button
            class="btn btn-primary btn-sm ui-mobile-12"
            :disabled="isArchived"
            @click="addPromocode"
          >
            + {{ $t('mediaBuying.campaigns.add_promocode') }}
          </button>
        </div>
      </template>
    </TableCard>
    <AddPromoCode
      ref="addCode"
      @add-promo-code="update"
    />
  </section>
</template>

<script>
import UrlMixin from '@/components/Query/url-mixin';
import UrlMixinHooks from '@/components/Query/url-mixin-hooks';
import InjectPlugins from '@/components/Report/Components/inject-plugins';
import { pageSizeMixin } from '@/views/Dashboard/Reports/page_size';
import AddPromoCode from '@/views/Dashboard/MediaBuying/Modal/AddPromoCode.vue';
import Formatters from '@/views/Dashboard/CustomReports/formaters';
import TableCard from '@/components/Cards/TableCard.vue';
import tableFlowMixinGenerator from '@/views/Dashboard/Reports/tableFlowMixin';
import TableCardSearchHeader from '@/components/Cards/TableCardSearchHeader.vue';
import UiTableAction from '@/components/ui/UiTableAction.vue';
import UiTablePopoverAction from '@/components/ui/UiTablePopoverAction.vue';
import copyMixin from '@/mixins/copy';

const defaultParams = {
  prop: 'created_at',
  order: 'desc',
  search: '',

  page: 1,
  pageSize: 20,
};

const tableFlowMixin = tableFlowMixinGenerator({
  defaultParams: {
    ...defaultParams,
  },
  params: Object.keys(defaultParams),
  sortByName: 'prop',
  sortDirName: 'order',
  fetchOnActivated: false,
  offset: 0,
  limit: defaultParams.pageSize,
});

export default {
  components: {
    AddPromoCode,
    TableCard,
    TableCardSearchHeader,
  },
  mixins: [
    copyMixin,
    UrlMixin,
    UrlMixinHooks,
    InjectPlugins,
    pageSizeMixin,
    tableFlowMixin,
  ],
  props: ['statusChannel', 'statusCampaign', 'activeTab', 'tableSorting'],
  emits: ['update'],
  data() {
    return {
      period: {
        from: moment()
          .startOf('month')
          .format('YYYY-MM-DD 00:00:00'),
        to: moment()
          .endOf('day')
          .format('YYYY-MM-DD 23:59:59'),
        period: 30,
      },
      columns: [
        {
          name: 'created_at',
          align: 'left',
          sortable: 'custom',
          filterable: false,
          width: 155,
          format: 'date-time',
          source: 'i18n',
          i18nPath: 'reports',
        },
        {
          name: 'promo_code',
          align: 'left',
          filterable: false,
          width: 250,
          copyable: true,
        },
        {
          name: 'affiliate_id',
          align: 'left',
          filterable: false,
          renderValue: (v) => `<span class='text-green'>${v}</span>`,
        },
        {
          name: 'registrations',
          align: 'right',
          filterable: false,
          sortable: 'custom',
          width: 130,
        },
        {
          name: 'players',
          align: 'right',
          filterable: false,
          sortable: 'custom',
          width: 130,
        },
      ],
      loading: false,
      formaters: Formatters,
      copyDone: false,
    };
  },

  computed: {
    rows() {
      return this.$store.state.promocode.promocodes.map((e) => {
        const subids = [];

        for (let i = 1; i <= 5; i++) {
          const key = `subid${i}`;
          subids.push({ value: e[key] });
        }

        const affiliate = this.$store.state.subaffiliates.all.find((i) => i.data.id === e.affiliate_id);
        let tail = false;
        while (!tail) {
          const item = subids.pop();

          if (item === undefined) {
            subids.push({ value: null });
            break;
          }

          tail = item.value;

          if (tail) {
            subids.push(item);
          }
        }

        return {
          data: {
            id: e.id,
            affiliateOfferId: e.affiliate_offer_id,
            subids,
            traffic_source_id: e.traffic_source_id,
          },
          promo_code: e.promo_code,
          affiliate_id: affiliate ? affiliate.email : '',
          sub_affiliate_id: e.affiliate_id,
          registrations: e.registrations,
          players: e.players,
          created_at: e.created_at,
        };
      });
    },
    isArchived() {
      return this.statusChannel === 'archived' || this.statusCampaign === 'archived';
    },
    actions() {
      const operations = [
        {
          name: 'statistics',
          componentAlt: {
            component: UiTableAction,
            props: (row) => {
                const urlQuery = {};
                urlQuery.openPromocodeReport = row;
                urlQuery.passedPeriod = this.period;
                const compressedUrlQuery = this.compressUrlQuery(urlQuery);

                return {
                  tooltipContent: this.$t('promoCodes.promocodes.statistics'),
                  iconClass: 'far fa-chart-bar',
                  colorPreset: 'info',
                  link: `#/dashboard/custom/${compressedUrlQuery}`,
                  onClick: () => {
                    this.$router.push({
                      name: 'routes.custom',
                      params: { urlQuery: compressedUrlQuery },
                    });
                  },
                };
              },
            },
        },
        {
          name: 'edit',
          componentAlt: {
            component: UiTableAction,
            props: (row) => ({
                tooltipContent: this.$t('promoCodes.promocodes.statistics'),
                iconClass: 'far fa-pen',
                colorPreset: 'green',
                onClick: async () => {
                  this.addPromocode(row);
                },
              }),
            },
        },
        {
          name: 'delete',
          componentAlt: {
            component: UiTablePopoverAction,
            props: (row) => ({
              tooltipContent: this.$t('promoCodes.promocodes.delete'),
              iconClass: 'far fa-trash-alt',
              colorPreset: 'red',
              width: 250,
              message: this.$t('mediaBuying.campaigns.delete_promocode_message'),
              buttons: [
                {
                  text: this.$t('mediaBuying.channels.cancel'),
                },
                {
                  text: this.$t('promoCodes.promocodes.delete'),
                  type: 'danger',
                  click: () => this.deleteKey(row.data.id),
                },
              ],
            }),
          },
        },
      ];

      return {
        width: 120,
        name: () => this.$t('profile.apiKeys.table.actions'),
        fixed: 'right',
        operations,
      };
    },
  },
  methods: {
    addPromocode(row) {
      this.$refs.addCode.open(row);
    },
    deleteKey(id) {
      this.$store.dispatch('deletePromoCode', id)
        .then(() => this.update());
    },
    handleSort({
      prop, order, sort_by, sort_dir,
    }) {
      const column = prop || sort_by;
      const dir = order || sort_dir;
      this.$emit('update', {
        tab: 'promocodes',
        prop: column,
        order: dir,
      });
    },
    update() {
      this.$emit('update', {
        tab: 'promocodes',
        prop: this.sorting.prop,
        order: this.sorting.order,
        search: this.search,

        limit: this.pageSize,
        offset: (this.page - 1) * this.pageSize,
      });
    },
    async fetchData() {
      this.update();
    },
  },
  created() {
    this.actions.operations.map((e) => {
      if (e.component) {
        e.component = e.component.bind(this);
      }
    });
  },
  mounted() {
    window.copyToClipboard = this.copy;
  },
  activated() {
    window.copyToClipboard = this.copy;
  },
  deactivated() {
    delete window.copyToClipboard;
  },
  watch: {
    activeTab() {
      if (this.activeTab === 'promocodes') {
        this.update();
      }
    },
    tableSorting: {
      immediate: true,
      deep: true,
      handler(value) {
        this.sorting = {
          prop: value.sort_column,
          order: value.sort_dir,
        };
      },
    },
  },
};
</script>
